import React, { useState, useEffect } from 'react';
import { Calendar, AlertCircle, MapPinOff, WifiOff, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
}

function fmtDur(mins) {
  if (mins == null) return '—';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins - h * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// Jun 30, 2026 — user asked that each "Out of Office (geo-fence exits)"
// row show whether the exit was a planned visit / break / lunch /
// permission / unplanned visit. Backend now tags every row with a ``kind``
// (see routes/staff_self.py _dominant_kind). Map it to a coloured pill so
// the staff can validate at-a-glance.
const KIND_LABEL = {
  visit: { text: 'Visit', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  break: { text: 'Break', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  lunch: { text: 'Lunch', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  permission: { text: 'Permission', cls: 'bg-purple-100 text-purple-800 border-purple-200' },
  non_plan: { text: 'Unplanned', cls: 'bg-red-100 text-red-800 border-red-200' },
  unplanned: { text: 'Unplanned', cls: 'bg-red-100 text-red-800 border-red-200' },
};
function kindPill(kind) {
  const v = KIND_LABEL[kind] || { text: 'Unknown', cls: 'bg-gray-100 text-gray-700 border-gray-200' };
  return (
    <span
      data-testid={`exit-kind-${kind || 'unknown'}`}
      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${v.cls}`}
    >
      {v.text}
    </span>
  );
}

export default function MissingLogTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const fetchData = async (d) => {
    setLoading(true); setErr(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/staff/missing-log?date=${d}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!r.ok) throw new Error(`Status ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(date); }, [date]);

  return (
    <div className="space-y-3" data-testid="missing-log-tab">
      <div className="bg-white rounded-xl p-3 shadow-sm flex items-center gap-2">
        <Calendar size={16} className="text-gray-500" />
        <input
          type="date"
          value={date}
          max={today}
          onChange={(e) => setDate(e.target.value)}
          className="flex-1 px-2 py-1 border rounded text-sm"
          data-testid="missing-log-date"
        />
        <button
          onClick={() => fetchData(date)}
          className="p-2 bg-gray-100 hover:bg-gray-200 rounded"
          data-testid="missing-log-refresh"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
          {err}
        </div>
      )}

      {/* GPS gaps — out of reach */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-2 bg-orange-50 border-b border-orange-100 flex items-center gap-2">
          <WifiOff size={16} className="text-orange-600" />
          <h3 className="font-semibold text-sm text-orange-900">Out of Reach (GPS gaps)</h3>
          <span className="ml-auto text-xs text-orange-700 font-medium">{data?.gps_gaps?.length || 0}</span>
        </div>
        {data?.gps_gaps?.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {data.gps_gaps.map((g, i) => (
              <li key={`gap-${g.from || ''}-${g.to || ''}-${i}`} className="px-4 py-2 flex items-center justify-between text-sm" data-testid={`gps-gap-row-${i}`}>
                <div>
                  <span className="font-mono">{fmtTime(g.from)} → {fmtTime(g.to)}</span>
                </div>
                <span className="text-orange-700 font-semibold">{fmtDur(g.duration_minutes)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-4 text-sm text-gray-500 text-center">No GPS gaps on this day.</p>
        )}
      </div>

      {/* Geofence exits — out of office */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-2 bg-red-50 border-b border-red-100 flex items-center gap-2">
          <MapPinOff size={16} className="text-red-600" />
          <h3 className="font-semibold text-sm text-red-900">Out of Office (geo-fence exits)</h3>
          <span className="ml-auto text-xs text-red-700 font-medium">{data?.geofence_exits?.length || 0}</span>
        </div>
        {data?.geofence_exits?.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {data.geofence_exits.map((g, i) => (
              <li key={`gex-${g.from || ''}-${g.to || ''}-${i}`} className="px-4 py-2 flex items-center justify-between text-sm" data-testid={`geo-exit-row-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono">{fmtTime(g.from)} → {fmtTime(g.to)}</span>
                  {kindPill(g.kind)}
                </div>
                <span className="text-red-700 font-semibold whitespace-nowrap">{fmtDur(g.duration_minutes)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-4 text-sm text-gray-500 text-center">No geo-fence exits on this day.</p>
        )}
      </div>

      {(data?.gps_gaps?.length === 0 && data?.geofence_exits?.length === 0) && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900 flex items-center gap-2">
          <AlertCircle size={16} className="text-emerald-600" />
          All clear for {date}.
        </div>
      )}
    </div>
  );
}
